import React from "react";
import { COLORS, FONT_BODY, FONT_DISPLAY, FONT_MONO } from "../theme.js";
import { conditionColor } from "../domain/format.js";
import { ghostBtnStyle } from "./styles.js";

/* ---------------- Logo ---------------- */
const LOGO_ASPECT = 304 / 500; // width / height of the source mark — keep in sync with the file

export function LogoMark({ size = 44 }) {
  return (
    <img
      src="/senogara_logo_gold.png"
      alt="SENOGARA"
      style={{ height: size, width: size * LOGO_ASPECT, display: "block", objectFit: "contain" }}
    />
  );
}

export function Wordmark({ size = 22, tagline = true }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
      <div style={{ fontFamily: FONT_DISPLAY, fontWeight: 600, fontSize: size, letterSpacing: size * 0.14, color: COLORS.goldBright }}>
        SENOGARA
      </div>
      {tagline && (
        <div style={{ fontFamily: FONT_MONO, fontSize: Math.max(9, size * 0.32), letterSpacing: 3, color: COLORS.creamFaint, marginTop: 6 }}>
          CONNECT · COLLECT · PRESERVE
        </div>
      )}
    </div>
  );
}

/* ---------------- Form atoms ---------------- */
export function Field({ label, children, style }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 6, ...style }}>
      <span style={{ fontSize: 11, fontFamily: FONT_BODY, color: COLORS.creamDim, letterSpacing: 0.3, textTransform: "uppercase" }}>{label}</span>
      {children}
    </label>
  );
}

/* ---------------- Layout ---------------- */
export function Section({ title, right, children }) {
  return (
    <div style={{ marginBottom: 22 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <span style={{ fontSize: 11, fontFamily: FONT_MONO, letterSpacing: 1.2, textTransform: "uppercase", color: COLORS.goldBright }}>{title}</span>
        {right}
      </div>
      {children}
    </div>
  );
}

export function Seal({ value, size = 56 }) {
  const color = conditionColor(value);
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: "50%",
        background: `conic-gradient(${color} ${value}%, ${COLORS.lineFaint} ${value}% 100%)`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flex: "none",
      }}
    >
      <div
        style={{
          width: size - 10,
          height: size - 10,
          borderRadius: "50%",
          background: COLORS.blackCard,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
        }}
      >
        <span style={{ fontSize: size * 0.28, fontWeight: 700, lineHeight: 1, color, fontFamily: FONT_MONO }}>{value}</span>
      </div>
    </div>
  );
}

export function Chip({ label, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        fontFamily: FONT_BODY,
        fontSize: 12.5,
        padding: "8px 13px",
        borderRadius: 8,
        cursor: "pointer",
        border: `1px solid ${active ? COLORS.gold : COLORS.line}`,
        background: active ? "rgba(201,162,39,0.16)" : "transparent",
        color: active ? COLORS.goldBright : COLORS.creamDim,
        fontWeight: active ? 600 : 400,
      }}
    >
      {label}
    </button>
  );
}

export function StatCard({ label, value, sub, accent }) {
  return (
    <div style={{ background: COLORS.blackCard, border: `1px solid ${COLORS.lineFaint}`, borderRadius: 14, padding: "16px 16px" }}>
      <div style={{ fontSize: 10.5, fontFamily: FONT_MONO, letterSpacing: 0.6, textTransform: "uppercase", color: COLORS.creamFaint }}>{label}</div>
      <div style={{ fontFamily: FONT_DISPLAY, fontSize: 24, color: accent || COLORS.cream, marginTop: 6 }}>{value}</div>
      {sub && <div style={{ fontSize: 11.5, color: COLORS.creamFaint, fontFamily: FONT_BODY, marginTop: 3 }}>{sub}</div>}
    </div>
  );
}

export function IconBadgeChip({ icon, label, earned }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6, opacity: earned ? 1 : 0.28, minWidth: 74 }}>
      <div
        style={{
          width: 46,
          height: 46,
          borderRadius: 10,
          border: `1px solid ${earned ? COLORS.goldBright : COLORS.line}`,
          background: earned ? COLORS.blackCard2 : "transparent",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 20,
        }}
      >
        {icon}
      </div>
      <span style={{ fontSize: 10.5, fontFamily: FONT_BODY, textAlign: "center", color: earned ? COLORS.cream : COLORS.creamFaint }}>{label}</span>
    </div>
  );
}

export function BadgeChip({ name, earned }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6, opacity: earned ? 1 : 0.28, minWidth: 64 }}>
      <div
        style={{
          width: 46,
          height: 46,
          borderRadius: "50%",
          border: `1px solid ${earned ? COLORS.goldBright : COLORS.line}`,
          background: earned ? `linear-gradient(145deg, ${COLORS.goldBright}, ${COLORS.gold})` : "transparent",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontFamily: FONT_DISPLAY,
          fontSize: 15,
          color: earned ? "#1A1408" : COLORS.creamFaint,
        }}
      >
        {name.slice(0, 2).toUpperCase()}
      </div>
      <span style={{ fontSize: 10.5, fontFamily: FONT_BODY, textAlign: "center", color: earned ? COLORS.cream : COLORS.creamFaint }}>{name}</span>
    </div>
  );
}

/* ---------------- Async-state atoms ----------------
   The prototype never needed these — nothing it displayed could fail or be
   in-flight. Every screen that now reads from Supabase uses them. */

export function Spinner({ size = 18, color = COLORS.goldBright }) {
  return (
    <>
      <span
        style={{
          width: size,
          height: size,
          borderRadius: "50%",
          border: `2px solid ${COLORS.lineFaint}`,
          borderTopColor: color,
          display: "inline-block",
          animation: "sp-spin 0.7s linear infinite",
          flex: "none",
        }}
      />
      <style>{"@keyframes sp-spin { to { transform: rotate(360deg); } }"}</style>
    </>
  );
}

export function LoadingPanel({ label = "Loading…" }) {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10, padding: "48px 0", color: COLORS.creamFaint, fontFamily: FONT_BODY, fontSize: 13 }}>
      <Spinner />
      {label}
    </div>
  );
}

export function ErrorNote({ message, onRetry, actionLabel = "Retry" }) {
  if (!message) return null;
  return (
    <div
      role="alert"
      style={{
        background: "rgba(180,69,63,0.10)",
        border: `1px solid ${COLORS.red}`,
        borderRadius: 12,
        padding: "12px 14px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 12,
      }}
    >
      <span style={{ fontSize: 12.5, fontFamily: FONT_BODY, color: COLORS.cream }}>{message}</span>
      {onRetry && (
        <button onClick={onRetry} style={{ ...ghostBtnStyle, fontSize: 12.5, color: COLORS.goldBright, flex: "none" }}>
          {actionLabel}
        </button>
      )}
    </div>
  );
}

/** Blocking confirmation for destructive, irreversible actions.
 *  `danger` paints the confirm button red rather than gold, so the affirmative
 *  action never looks like the routine one. */
export function ConfirmDialog({ title, body, confirmLabel = "Confirm", cancelLabel = "Cancel", busy, onConfirm, onCancel }) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: "fixed", inset: 0, zIndex: 60, background: "rgba(0,0,0,0.78)",
        display: "flex", alignItems: "center", justifyContent: "center", padding: 24,
      }}
      onClick={busy ? undefined : onCancel}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: COLORS.blackCard, border: `1px solid ${COLORS.line}`, borderRadius: 16,
          padding: 22, maxWidth: 340, width: "100%", display: "flex", flexDirection: "column", gap: 10,
        }}
      >
        <div style={{ fontFamily: FONT_DISPLAY, fontSize: 18, color: COLORS.cream }}>{title}</div>
        {body && <div style={{ fontFamily: FONT_BODY, fontSize: 12.5, color: COLORS.creamDim, lineHeight: 1.6 }}>{body}</div>}
        <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
          <button
            onClick={onCancel}
            disabled={busy}
            style={{
              flex: 1, background: "transparent", color: COLORS.cream, border: `1px solid ${COLORS.line}`,
              borderRadius: 9, padding: "12px 16px", fontSize: 14, fontWeight: 600, fontFamily: FONT_BODY,
              cursor: busy ? "not-allowed" : "pointer", opacity: busy ? 0.45 : 1,
            }}
          >
            {cancelLabel}
          </button>
          <button
            onClick={onConfirm}
            disabled={busy}
            style={{
              flex: 1, background: COLORS.red, color: COLORS.cream, border: "none",
              borderRadius: 9, padding: "12px 16px", fontSize: 14, fontWeight: 700, fontFamily: FONT_BODY,
              cursor: busy ? "not-allowed" : "pointer", opacity: busy ? 0.6 : 1,
              display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
            }}
          >
            {busy && <Spinner size={13} color={COLORS.cream} />}
            {busy ? "Deleting…" : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

export function EmptyState({ title, body, action }) {
  return (
    <div style={{ textAlign: "center", padding: "36px 12px", display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
      <div style={{ fontFamily: FONT_DISPLAY, fontSize: 17, color: COLORS.cream }}>{title}</div>
      {body && <div style={{ fontSize: 12.5, fontFamily: FONT_BODY, color: COLORS.creamFaint, maxWidth: 280, lineHeight: 1.5 }}>{body}</div>}
      {action}
    </div>
  );
}
